import { CellContext } from '@tanstack/react-table';
import { CompanyCustomBenefitAssignmentEmployee } from '@pxtra-client-lib-legacy/gen';
import { Button } from '../../../atomic/button';
import { ArrowDownIcon } from '../../../../icons';
import { Box, ClickAwayListener, Divider, Popper, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Util from '../../../../../misc/util';
import { RoundedPaper } from '../../../atomic/surface';

export const CurrentBenefitsCell = ({
  row
}: CellContext<CompanyCustomBenefitAssignmentEmployee, unknown>) => {
  const { t } = useTranslation();
  const employee = row.original;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const benefitBudget = employee.available_benefit_budget ?? 0;
  const customBenefitSum = Number(employee.monthly_custom_benefit_sum ?? 0);
  const totalCents = benefitBudget + customBenefitSum;

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const rows = [
    { key: 'benefit_budget', name: t('custom:benefit_budget'), budget: benefitBudget },
    { key: 'other_benefits', name: t('custom:other_benefits'), budget: customBenefitSum }
  ];

  return (
    <>
      <Button variant="tertiary" size="small" onClick={handleClick}>
        <span
          style={{
            textAlign: 'right',
            minWidth: '70px',
            fontSize: 16,
            display: 'inline-block'
          }}
        >
          {Util.formatCurrency(totalCents)}
        </span>
        <ArrowDownIcon />
      </Button>
      <Popper open={!!anchorEl} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <RoundedPaper
            sx={{
              width: 320,
              height: 'fit-content',
              maxHeight: 420,
              minHeight: 80,
              background: 'white',
              position: 'relative'
            }}
            withShadow
          >
            <Box sx={{ padding: '24px' }}>
              <Typography fontWeight="bold">
                {t('custom:current_benefits', { count: 0 })}
              </Typography>
              <Typography>
                {employee.first_name} {employee.last_name}
              </Typography>
            </Box>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gridTemplateColumns: '50% 15% 15% 20%',
                gridRowGap: '4px',
                maxHeight: '318px',
                width: '100%',
                padding: '8px'
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gridAutoRows: '36px',
                  gridColumn: '1 / 3',
                  gridRow: '1 / 2',
                  paddingRight: '17px'
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'inherit',
                    gridColumn: '1 / 3',
                    paddingX: '16px',
                    paddingY: '6px'
                  }}
                >
                  <Typography
                    sx={{
                      gridColumn: '1 / 2',
                      gridRow: '1 / 2',
                      color: 'grey.900',
                      fontSize: '12px',
                      display: 'inline',
                      fontWeight: 'bold',
                      textAlign: 'left'
                    }}
                  >
                    {t('common:BENEFITS')}
                  </Typography>
                  <Typography
                    sx={{
                      gridColumn: '2 / 3',
                      gridRow: '1 / 2',
                      color: 'grey.900',
                      fontSize: '12px',
                      display: 'inline',
                      fontWeight: 'bold',
                      textAlign: 'right'
                    }}
                  >
                    {t('common:BUDGET')}
                  </Typography>
                </Box>
              </Box>
              {/* Body */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gridGap: 'inherit',
                  gridAutoRows: '36px',
                  gridColumn: '1 / 3',
                  overflow: 'auto',
                  paddingRight: '17px',
                  width: '95%'
                }}
              >
                {rows.map(({ key, name, budget }, index) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'inherit',
                      gridColumn: '1 / 3',
                      gridRow: `${index + 1} / ${index + 2}`,
                      backgroundColor: index % 2 === 1 ? 'grey.50' : '',
                      borderRadius: '8px',
                      paddingX: '16px',
                      paddingY: '6px'
                    }}
                  >
                    <Typography
                      sx={{
                        gridColumn: '1 / 2',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        fontSize: 'smaller'
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      align="right"
                      sx={{
                        gridColumn: '2 / 3',
                        fontSize: 'smaller'
                      }}
                    >
                      {Util.formatCurrency(budget)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </RoundedPaper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
